import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { MapPin, Plane, Lock, Globe2, Check } from "lucide-react";
import HubMap from "./HubMap";

const REGION_COLORS = {
  americas: { bg: "bg-emerald-50", border: "border-emerald-300", accent: "text-emerald-700", label: "Americas" },
  europe: { bg: "bg-sky-50", border: "border-sky-300", accent: "text-sky-700", label: "Europe" },
  north_america: { bg: "bg-violet-50", border: "border-violet-300", accent: "text-violet-700", label: "North America" },
  middle_east: { bg: "bg-amber-50", border: "border-amber-300", accent: "text-amber-700", label: "Middle East" },
  asia: { bg: "bg-rose-50", border: "border-rose-300", accent: "text-rose-700", label: "Asia" },
};

const BAND_LABELS = {
  R01: "Short-haul 1", R02: "Short-haul 2", R03: "Medium 1",
  R04: "Medium 2", R05: "Medium 3", R06: "Long-haul 1",
  R07: "Long-haul 2", R08: "Regional",
};

export default function HubSelection({ gameId, teamId, onHubSelected }) {
  const { t } = useTranslation();
  const [hubs, setHubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selecting, setSelecting] = useState(null);
  const [expandedHub, setExpandedHub] = useState(null);

  useEffect(() => {
    axios.get(`${API}/games/${gameId}/hubs`)
      .then(res => setHubs(res.data))
      .catch(err => console.error("HubSelection:", err.message))
      .finally(() => setLoading(false));
  }, [gameId]);

  const handleSelect = async (hubCode) => {
    setSelecting(hubCode);
    try {
      const res = await axios.post(`${API}/teams/${teamId}/select-hub`, { hub_code: hubCode });
      toast.success(t("hub.selectedToast", { name: res.data.hub_name, code: hubCode }));
      if (res.data.game_started) {
        toast.success(t("hub.allHubsChosen"), { duration: 8000 });
      }
      onHubSelected(hubCode);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to select hub");
    } finally {
      setSelecting(null);
    }
  };

  // Group by region
  const regions = {};
  hubs.forEach(h => {
    const r = h.region || "other";
    if (!regions[r]) regions[r] = [];
    regions[r].push(h);
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="w-8 h-8 border-4 border-slate-300 border-t-sky-500 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-8 space-y-8" data-testid="hub-selection">
      <div className="text-center space-y-2">
        <Globe2 className="w-10 h-10 text-sky-600 mx-auto" />
        <span className="inline-block px-3 py-1 rounded-full bg-amber-100 text-amber-800 text-xs font-bold uppercase tracking-wider" data-testid="decision-zero-badge">
          {t("hub.decisionZero")}
        </span>
        <h1 className="text-2xl font-heading font-bold text-slate-900">{t("hub.title")}</h1>
        <p className="text-slate-500 max-w-lg mx-auto">{t("hub.subtitle")}</p>
        <p className="text-xs text-slate-400 max-w-lg mx-auto">{t("hub.exclusiveHint")}</p>
      </div>

      <HubMap
        hubs={hubs.filter(h => !h.taken)}
        selectedCode={expandedHub}
        onPickHub={(code) => {
          setExpandedHub(code);
          const el = document.querySelector(`[data-testid="hub-card-${code}"]`);
          if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
        }}
      />

      {Object.entries(regions).map(([region, regionHubs]) => {
        const style = REGION_COLORS[region] || REGION_COLORS.europe;
        return (
          <div key={region} className="space-y-3">
            <h2 className={`text-sm font-bold uppercase tracking-wider ${style.accent}`}>{style.label}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {regionHubs.map(hub => {
                const taken = hub.taken;
                const isExpanded = expandedHub === hub.code;
                return (
                  <Card
                    key={hub.code}
                    className={`transition-all cursor-pointer ${taken ? "opacity-60 border-slate-200" : `${style.border} hover:shadow-lg`} ${isExpanded ? "ring-2 ring-sky-400" : ""}`}
                    onClick={() => !taken && setExpandedHub(isExpanded ? null : hub.code)}
                    data-testid={`hub-card-${hub.code}`}
                  >
                    <CardHeader className={`py-3 ${taken ? "bg-slate-50" : style.bg}`}>
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-base flex items-center gap-2">
                          <MapPin className="w-4 h-4" />
                          <span className="font-mono font-bold">{hub.code}</span>
                          <span className="font-normal">{hub.name}</span>
                        </CardTitle>
                        {taken && (
                          <span className="flex items-center gap-1 text-xs text-slate-500">
                            <Lock className="w-3 h-3" /> {hub.taken_by}
                          </span>
                        )}
                      </div>
                    </CardHeader>
                    <CardContent className="py-3 space-y-2">
                      {isExpanded && !taken && (
                        <>
                          <p className="text-xs text-slate-500 font-semibold uppercase tracking-wider mb-2">{t("hub.routePreview")}</p>
                          <div className="space-y-1">
                            {Object.entries(hub.route_previews || {}).map(([band, opts]) => (
                              <div key={band} className="flex items-start gap-2 text-xs">
                                <span className="font-mono text-slate-400 w-8 shrink-0">{band}</span>
                                <div className="flex-1">
                                  {opts.map((o, i) => (
                                    <span key={o.code}>
                                      {i > 0 && <span className="text-slate-300 mx-1">|</span>}
                                      <span className="text-slate-700">{o.city}</span>
                                      <span className="text-slate-400 ml-0.5">({o.distance_km}km)</span>
                                    </span>
                                  ))}
                                </div>
                              </div>
                            ))}
                          </div>
                          <Button
                            onClick={(e) => { e.stopPropagation(); handleSelect(hub.code); }}
                            disabled={selecting === hub.code}
                            className="w-full mt-3 gap-2"
                            data-testid={`select-hub-${hub.code}`}
                          >
                            <Check className="w-4 h-4" />
                            {selecting === hub.code ? "..." : t("hub.selectHub")}
                          </Button>
                        </>
                      )}
                      {!isExpanded && !taken && (
                        <p className="text-xs text-slate-400 text-center">{t("hub.clickToExpand")}</p>
                      )}
                      {taken && (
                        <p className="text-xs text-slate-400 text-center italic">{t("hub.alreadyTaken")}</p>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}
